import { useState } from 'react';
import { Button, Input, Select, toast } from '@/components/ui';
import { friendlyMessage } from '@/lib/errors';
import { useActiveCouriers } from '@/features/shipping/couriers/hooks/useShippingCouriers';
import {
  ORDER_STATUS_TARGETS,
  ORDER_STATUS_TARGET_LABELS,
  useOrderStatusChangeMutation,
  type OrderStatusChangeResult,
  type OrderStatusTarget,
} from '../hooks/useOrderStatusChangeMutation';

/**
 * QA-457 — 주문 상태 변경(주문 단위, 부분 출고 없음). 주문 상세(1건)와 주문 목록 일괄(최대 100건)이 같이 쓴다.
 * 배송중은 택배사·송장번호를 넣을 수 있다(비우면 주문에 등록된 값). 구매확정은 OPS·SUPER 만(BE 도 403).
 * 주문마다 성공·실패가 따로 오므로 실패한 주문은 사유와 함께 남겨 보여 준다.
 */
export const OrderStatusChangeForm = ({
  orderIds,
  allowPurchaseConfirm,
  allowCancel = false,
  onDone,
}: {
  orderIds: ReadonlyArray<string>;
  allowPurchaseConfirm: boolean;
  /** [취소](입금대기 주문 취소) — 운영 관리자·고객 상담·최고 관리자만. */
  allowCancel?: boolean;
  onDone?: () => void;
}) => {
  const change = useOrderStatusChangeMutation();
  const [target, setTarget] = useState<OrderStatusTarget | ''>('');
  // QA-510 택배사는 배송 업체 관리의 "사용" 업체(순서대로), 기본은 맨 위 업체. [택배사(등록된 값)]을 고르면 주문에 등록된 값.
  const { couriers } = useActiveCouriers();
  const [pickedCourier, setCourierCode] = useState<string | null>(null);
  const courierCode = pickedCourier ?? couriers[0]?.courierCode ?? '';
  const [trackingNo, setTrackingNo] = useState('');
  const [failures, setFailures] = useState<OrderStatusChangeResult[]>([]);
  // 구매확정·환불완료(QA-529)는 운영 관리자·최고 관리자만(allowPurchaseConfirm).
  const targets = ORDER_STATUS_TARGETS.filter(
    (s) =>
      (allowPurchaseConfirm || (s !== 'PURCHASE_CONFIRMED' && s !== 'REFUND_DONE')) && (allowCancel || s !== 'CANCELED'),
  );

  const run = () => {
    if (!target) return;
    const label = ORDER_STATUS_TARGET_LABELS[target];
    const who = orderIds.length > 1 ? `주문 ${orderIds.length}건을` : '이 주문을';
    // QA-529 환불완료는 상태만 맞춘다 — 실제 환불(PG)은 결제 관리·PG 관리자에서 먼저 끝나 있어야 한다.
    const note = target === 'REFUND_DONE' ? '\n\n결제 환불(PG)은 하지 않습니다. 결제가 이미 환불된 주문만 바꿔 주세요.' : '';
    // 입금대기 취소 — BE 문구 그대로. 입금대기(무통장·가상계좌)가 아닌 주문은 주문별 실패 사유로 온다.
    const question =
      target === 'CANCELED'
        ? `${who} 취소할까요?\n\n입금 전 주문을 취소합니다. 가상계좌는 발급된 계좌도 함께 닫혀 고객이 더 이상 입금할 수 없습니다.`
        : `${who} "${label}"(으)로 바꿀까요?${note}`;
    if (!window.confirm(question)) return;
    setFailures([]);
    change.mutate(
      {
        orderIds,
        targetStatus: target,
        ...(target === 'SHIPPING' && courierCode ? { courierCode } : {}),
        ...(target === 'SHIPPING' && trackingNo.trim() ? { trackingNo } : {}),
      },
      {
        onSuccess: (res) => {
          const failed = res.results.filter((r) => !r.success);
          setFailures(failed);
          if (res.failCount === 0) {
            const done = target === 'CANCELED' ? '주문을 취소했습니다.' : `"${label}"(으)로 바꿨습니다.`;
            toast.success(`${done}${res.successCount > 1 ? ` (${res.successCount}건)` : ''}`);
            onDone?.();
          } else if (res.successCount > 0) {
            toast.warning(`${res.successCount}건 변경, ${res.failCount}건 실패 — 아래 사유를 확인하세요.`);
          } else {
            toast.error(failed[0]?.message ?? '주문 상태를 바꾸지 못했습니다.');
          }
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-2" data-testid="order-status-change">
      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <Select
          aria-label="변경할 주문상태"
          value={target}
          onChange={(e) => setTarget(e.target.value as OrderStatusTarget | '')}
          className="sm:w-48"
          data-testid="order-status-change-select"
        >
          <option value="">변경할 주문상태 선택</option>
          {targets.map((s) => (
            <option key={s} value={s}>
              {ORDER_STATUS_TARGET_LABELS[s]}
            </option>
          ))}
        </Select>
        {target === 'SHIPPING' ? (
          <>
            <Select
              aria-label="택배사"
              value={courierCode}
              onChange={(e) => setCourierCode(e.target.value)}
              className="sm:w-40"
              data-testid="order-status-change-courier"
            >
              <option value="">택배사(등록된 값)</option>
              {couriers.map((c) => (
                <option key={c.courierCode} value={c.courierCode}>
                  {c.nameKo}
                </option>
              ))}
            </Select>
            <Input
              aria-label="송장번호"
              placeholder="송장번호(비우면 등록된 값)"
              value={trackingNo}
              onChange={(e) => setTrackingNo(e.target.value)}
              className="sm:w-56"
              data-testid="order-status-change-tracking"
            />
          </>
        ) : null}
        <Button
          type="button"
          variant="primary"
          size="md"
          disabled={!target || change.isPending}
          onClick={run}
          data-action="order-status-change"
        >
          {change.isPending ? '변경 중…' : '변경'}
        </Button>
      </div>
      <p className="text-[12px]" style={{ color: 'var(--content-muted)' }}>
        주문 단위로 바뀝니다(상품별 부분 출고 없음). 배송중은 택배사·송장번호가 필요하며, 비우면 주문에 등록된 값을 씁니다.
      </p>
      {change.error ? (
        <p role="alert" className="text-[12px]" style={{ color: 'var(--status-danger-fg)' }}>
          {friendlyMessage(change.error, '주문 상태를 바꾸지 못했습니다.')}
        </p>
      ) : null}
      {failures.length > 0 ? (
        <ul className="flex flex-col gap-1 text-[12px]" style={{ color: 'var(--status-danger-fg)' }} data-testid="order-status-change-failures">
          {failures.map((f) => (
            <li key={f.orderId}>
              {f.orderNo}: {f.message ?? '변경 실패'}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};
